import { Interactor } from "./entities/missionMap/missionMap";

/**
 * Partial-acceptance detection.
 *
 * Musicians routinely accept a whole call and then explain in the free-text
 * message that they can only play part of it ("I have a session the morning of
 * the 17th from 9-1"). The contractor sees a clean green Accept and only finds
 * the caveat later, if at all. This module reads the message the musician typed
 * and reports whether it looks like a *partial* acceptance, so the play page can
 * steer them to Hold before the answer goes out.
 *
 * The heuristic deliberately needs TWO independent signals rather than "does it
 * contain a number or a weekday": on their own, dates and times are far too
 * common in perfectly happy messages ("Thanks, see you Monday!", "Excited for
 * Beethoven 5"). So a message trips the detector when it contains either
 *
 *  - a DECISIVE phrase — wording that only ever means "not all of it"
 *    ("all but", "can't make", "need a sub", "out of town"), or
 *  - a LIMITATION cue AND a WHEN reference — a hedge or conflict word next to a
 *    concrete date/time ("conflict" + "the 17th", "only" + "Tuesday").
 *
 * Matches that sit right after a negation ("no conflicts", "don't have any
 * conflicts") are dropped, since those say the opposite.
 *
 * English only, which is what the play page ships today.
 */

/** How many days/times to quote back to the musician. */
const MAX_QUOTED = 3;

/**
 * Guards the ordinal patterns below: "the 2nd chair", "3rd stand" and "the 4th
 * movement" are positions in the orchestra or the score, not days of the month.
 * Shared by both ordinal readings so they cannot drift apart.
 */
const NOT_A_DATE =
  "(?!\\s+(?:chairs?|stands?|parts?|desks?|movements?|measures?|bars?|pages?|rows?|violins?|violas?|cellos?|horns?|trumpets?|trombones?|flutes?|clarinets?|oboes?|bassoons?|percussions?))";

/**
 * Concrete date and time references: weekdays, months, days of the month, clock
 * times and ranges, parts of the day, and "the first rehearsal"-style pointers
 * at one service inside a run.
 */
const WHEN_PATTERNS: RegExp[] = [
  // Weekdays spelled out, plus plurals ("no Mondays").
  /\b(?:mon|tues|wednes|thurs|fri|satur|sun)days?\b/gi,
  // Unambiguous abbreviations.
  /\b(?:tues|weds|thur|thurs)\b/gi,
  // The three-letter ones only count when actually abbreviated, so "I sat in
  // the back" is not read as a Saturday.
  /\b(?:mon|tue|wed|thu|fri|sat|sun)\./gi,
  // Months. "May" and "March" are left out of the bare list on purpose — here
  // they are modal verbs and marching bands far more often than months; both
  // still match when followed by a day.
  /\b(?:january|february|april|june|july|august|september|october|november|december)\b/gi,
  /\b(?:jan|feb|mar|apr|may|jun|jul|aug|sept?|oct|nov|dec)\.?\s+\d{1,2}(?:st|nd|rd|th)?\b/gi,
  // Days of the month: "the 17th", "the 17", "3rd". The first lookahead leaves
  // "the 3/14" to the numeric-date pattern below rather than clipping it to
  // "the 3".
  new RegExp(
    `\\bthe\\s+\\d{1,2}(?:st|nd|rd|th)?\\b(?![/-]?\\d)${NOT_A_DATE}`,
    "gi",
  ),
  new RegExp(`\\b\\d{1,2}(?:st|nd|rd|th)\\b${NOT_A_DATE}`, "gi"),
  // Numeric dates and hour ranges: "3/14", "3-14-26", "9-1".
  /\b\d{1,2}[/-]\d{1,2}(?:[/-]\d{2,4})?\b/g,
  /\b\d{4}-\d{1,2}-\d{1,2}\b/g,
  // Clock times: "9am", "9:30", "7:30 pm".
  /\b\d{1,2}(?::\d{2})?\s*(?:am|pm|a\.m\.|p\.m\.)/gi,
  /\b\d{1,2}:\d{2}\b/g,
  // Parts of the day and near-term relatives.
  /\b(?:mornings?|afternoons?|evenings?|matinees?|midday|noon|tonight|tomorrow|overnight|weekends?)\b/gi,
  /\b(?:next|this|last)\s+(?:week|weekend|month|year)\b/gi,
  // How musicians most often point at the call without naming a date at all:
  // "a conflict that week", "out of town those dates".
  /\b(?:that|those|these)\s+(?:weeks?|weekends?|days?|dates?|nights?|services?|sessions?|rehearsals?|concerts?|shows?|calls?)\b/gi,
  // "available for some of the earlier dates", "free for the remaining services".
  /\b(?:earlier|later|remaining|other|rest of the)\s+(?:dates?|days?|services?|sessions?|rehearsals?|concerts?|shows?|calls?|weeks?)\b/gi,
  // One service inside the run: "the first rehearsal", "last two concerts".
  /\b(?:first|1st|second|2nd|third|3rd|fourth|last|final|opening|closing)\s+(?:\w+\s+){0,2}(?:rehearsals?|services?|concerts?|sessions?|shows?|calls?|performances?|dress|dates?|days?|nights?|sets?|gigs?)\b/gi,
  /\b(?:first|second)\s+half\b/gi,
];

/**
 * Hedges, conflicts and limits. Any one of these next to a WHEN reference reads
 * as a partial acceptance; alone they are too weak to act on.
 */
const LIMITATION_PATTERNS: RegExp[] = [
  /\bcan'?t\b|\bcannot\b|\bunable\b|\bnot able\b/gi,
  /\bconflicts?\b|\bconflicting\b/gi,
  /\bunavailable\b|\bnot available\b|\bnot free\b/gi,
  /\bonly\b/gi,
  /\brelease[ds]?\b|\bexcuse[ds]?\b/gi,
  /\baway\b/gi,
  /\boverlaps?\b|\boverlapping\b/gi,
  // "part of"/"some of" only when it is the CALL being carved up. Without the
  // object they read as praise — "excited to be part of this year's festival".
  /\bpartial(?:ly)?\b/gi,
  /\b(?:some|most|half|none) of\s+(?:the|this|that|those|these)\s+(?:[\w']+\s+){0,2}?(?:call|run|set|weeks?|dates?|days?|services?|sessions?|rehearsals?|concerts?|shows?|schedule|program)\b/gi,
  // "part of" without the article is partial availability; "a part of" is
  // belonging — "looking forward to being a part of these rehearsals".
  /\bpart of the (?:call|run|set|week|dates|days|services|sessions|schedule|program)\b/gi,
  /\bother than\b|\bapart from\b|\baside from\b/gi,
  // Only about shifting a commitment. Bare "move" is usually the music —
  // "can I move over to play that piece as 2/picc".
  /\bdepends?\b|\bdepending\b|\bunless\b/gi,
  /\bmoveable\b|\bmovable\b|\bbe moved\b|\bmove\s+(?:the|my|that|this)\s+(?:trip|session|gig|rehearsal|call|date|service|concert|show|appointment|flight|schedule)\b/gi,
  // Hedges about being able to play, as opposed to "not sure what to wear".
  /\bmight not\b|\bmay not\b|\bprobably not\b|\bnot sure (?:i|if i|whether i|that i)\b/gi,
  /\bsplit\b|\bshift\b|\bswap\b/gi,
  // "I'm booked until 3 that day", "I'm at Sony until 5".
  /\bbooked until\b|\b(?:i'?m|i am|i'?ll be)\s+(?:in|at|on)\b[^.!?]{0,30}?\buntil\b/gi,
  // "I have a recording session", "I teach a class", "already booked a gig".
  // The window is generous on purpose: "an uncharacteristic 6:15pm start time
  // to a concert commitment" needs more than 40 characters of slack.
  /\b(?:have|has|had|got|booked|booking|playing|teach(?:ing|es)?|recording|auditioning|scheduled|called|i'm on|i am on|i'm at|i am at|i'll be on|i'll be at)\b[^.!?]{0,60}?\b(?:sessions?|gigs?|rehearsals?|recordings?|concerts?|services?|shows?|calls?|class(?:es)?|lessons?|commitments?|bookings?|jobs?|weddings?|tours?|auditions?|performances?|obligations?|demos?|matinees?|doubles?|triples?)\b/gi,
];

/**
 * Wording that means "not all of it" on its own, with no date needed.
 */
const DECISIVE_PATTERNS: RegExp[] = [
  // "but not sure" hedges about something else; "second but not EH?" asks which
  // part to play; "but not everyone likes iPads" is not about dates at all.
  /\ball but\b|\ball except\b|\bexcept\b|\bbut not\b(?!\s+(?:sure|certain|positive|clear|e\.?h\.?\b|english horn|first|second|third|fourth|principal|assistant|piccolo|picc\b|utility|everyone|everybody|all\b|many|most|much|seeing|reading|sure))/gi,
  /\bcan only (?:do|make|play|come|be|attend)\b|\bonly (?:do|make|play|available for|able to)\b/gi,
  // The possessive guard keeps "can't do my ceiling photo" out of it.
  /\b(?:can'?t|cannot) (?:make|play|attend|be there|come)\b|\b(?:can'?t|cannot) do\b(?!\s+(?:my|your|his|her|their|our)\b)/gi,
  /\b(?:won'?t|wouldn'?t|couldn'?t|can'?t) be able\b|\bwon'?t be able\b|\bnot able to (?:make|do|play|attend|be)\b/gi,
  /\bwon'?t make it\b|\bcan'?t make it\b|\bwouldn'?t make it\b|\bcouldn'?t make it\b/gi,
  // "Can I split this day please?"
  /\bsplit\s+(?:it\b|this|the|that|my)\s*(?:day|date|week|set|run|services?|calls?|sessions?)?\b/gi,
  /\bconflicts? with\b|\bhave a conflict\b|\bschedul(?:e|ing) conflict\b|\bdouble[- ]?booked\b/gi,
  // Needing a sub, never being one — half these musicians are the sub, and say
  // so ("thank you for the opportunity to play substitute cello").
  /\bneeds? a sub\b|\bfind a sub\b|\bget a sub\b|\bneed to sub\b|\bsub(?:bed)? out\b/gi,
  /\bout of town\b|\bon tour\b/gi,
  // "too late to accept" and "sorry for the late reply" are about the reply.
  /\bleave early\b|\bleaving early\b|\barrive late\b|\barriving late\b|\brunning late\b|\bbe late\b|\blate (?:to|for) (?:the|my|that|this|a|one|first|second|last|\d)\b/gi,
  /\b(?:a bit|a little|a few minutes|slightly|\d+\s*min(?:ute)?s?)\s+late\b/gi,
  // Only counts when what is being missed is a service or a date, and never
  // when it is sheet music — "the part is missing a page" is not a conflict.
  /\bmiss(?:ing)?\s+(?!(?:[\w']+\s+){0,2}(?:pages?|bars?|measures?|movements?|parts?|notes?|beats?|rests?)\b)(?:[\w']+\s+){0,3}?(?:rehearsals?|services?|concerts?|sessions?|shows?|calls?|performances?|dress|dates?|matinees?|nights?|gigs?|\d{1,2}(?:st|nd|rd|th)\b|\d{1,2}[/-]\d{1,2}\b)/gi,
  /\bnot available for\b|\bunavailable for\b/gi,
  // "I will accept the week minus the first Sunday reh."
  /\bminus\b|\bwith the exception of\b|\bother than the\b/gi,
];

/** The default pipeline paints Accept green and Hold blue. */
const ACCEPT_COLOR = "#4caf50";
const HOLD_COLOR = "#2196f3";

export type PartialAcceptance = {
  /** True when the message reads like the musician can only play part of the call. */
  partial: boolean;
  /** Date/time snippets quoted verbatim from the message, for the warning. */
  when: string[];
};

/**
 * Folds the punctuation phones actually produce onto the ASCII forms the
 * patterns are written in. Musicians reply from their phones, where curly
 * apostrophes outnumber straight ones better than two to one — without this,
 * "can’t" and "won’t" simply never match. En and em dashes become hyphens so
 * "3–5:30pm" reads as a time range.
 *
 * Every substitution is one character for one, so match offsets still line up
 * with the text the musician typed and quoted snippets stay faithful to it.
 *
 * @param {string} message The raw message.
 * @returns {string} The message with quotes and dashes normalized.
 */
function normalize(message: string): string {
  return message.replace(/[‘’‛´`]/g, "'").replace(/[–—−]/g, "-");
}

/**
 * Reports whether a match is cancelled by the words right in front of it:
 * negated ("no conflicts", "I don't have any conflicts") or positively framed
 * ("I look forward to playing with them on these services", which otherwise
 * reads as a booking clash).
 *
 * Only the current clause is inspected — "no problem, but I can't make the
 * 17th" must still count.
 *
 * @param {string} message The whole message.
 * @param {number} index Index the match starts at.
 * @returns {boolean} True when the match is cancelled by what precedes it.
 */
function isNegated(message: string, index: number): boolean {
  const clause =
    message
      .slice(0, index)
      .split(/[.!?,;:]/)
      .pop() ?? "";
  return /\b(?:no|not|never|without|zero|any|forward to|wait to)\s+(?:[\w'-]+\s+)?$/i.test(
    clause,
  );
}

/**
 * Collects every non-negated match of the given patterns.
 *
 * @param {string} message The message to scan.
 * @param {RegExp[]} patterns Patterns to run, each with the global flag.
 * @returns {string[]} Matched snippets, in order, trimmed and deduped.
 */
function collect(message: string, patterns: RegExp[]): string[] {
  const found: { text: string; start: number; end: number }[] = [];

  for (const pattern of patterns) {
    for (const match of message.matchAll(pattern)) {
      if (match.index === undefined) continue;
      if (isNegated(message, match.index)) continue;
      const text = match[0].trim();
      if (!text) continue;
      const start = match.index + match[0].indexOf(text);
      found.push({ text, start, end: start + text.length });
    }
  }

  // Patterns overlap by design — "the 3" and "3/14" both match "the 3/14", and
  // "the 4th" also matches as "4th". Keep the longest reading of each span.
  const kept: typeof found = [];
  for (const m of [...found].sort(
    (a, b) => b.text.length - a.text.length || a.start - b.start,
  )) {
    if (kept.some((k) => m.start < k.end && k.start < m.end)) continue;
    kept.push(m);
  }

  return kept.sort((a, b) => a.start - b.start).map((m) => m.text);
}

/**
 * Reads a musician's free-text message and reports whether it looks like they
 * can only play part of the call they are about to accept.
 *
 * @param {string} message What the musician typed. Empty is never partial.
 * @returns {PartialAcceptance} The verdict, with the dates/times to quote back.
 */
export function detectPartialAcceptance(message?: string): PartialAcceptance {
  const text = normalize((message ?? "").trim());
  if (text.length < 3) return { partial: false, when: [] };

  const when = collect(text, WHEN_PATTERNS);
  const decisive = collect(text, DECISIVE_PATTERNS).length > 0;
  const limited = decisive || collect(text, LIMITATION_PATTERNS).length > 0;

  return {
    partial: decisive || (limited && when.length > 0),
    when: when.slice(0, MAX_QUOTED),
  };
}

/**
 * Pulls together everything the musician typed into the free-text fields of the
 * answer being composed.
 *
 * Reads the inspectors being composed rather than a stage's interactor list, so
 * it works the same for the invite, for a message opened from the inbox, and
 * from the confirm bar — none of which have the same interactor set in scope.
 *
 * @param {object} inspectors The answers being composed, keyed by interactor ID.
 * @returns {string} The typed text, one field per line.
 */
export function typedMessage(inspectors?: {
  [id: number]: { kind?: string; message?: unknown };
}): string {
  return Object.values(inspectors ?? {})
    .filter((i) => i.kind === "text" && typeof i.message === "string")
    .map((i) => i.message as string)
    .join("\n");
}

/**
 * Normalizes a pipeline colour for comparison.
 *
 * @param {string} color The interactor colour.
 * @returns {string} Lowercased, trimmed colour.
 */
function normalizeColor(color?: string): string {
  return (color ?? "").trim().toLowerCase();
}

/**
 * Reports whether an interactor is the one that fully accepts the call.
 *
 * Interactors are pipeline-authored, so their colour and name are all the play
 * page gets — mercury's `keyName` column is opt-in and empty on every stock
 * pipeline. Every pipeline is cloned from the default one, where Accept is
 * green, so the colour is the primary signal and the name covers pipelines that
 * were recoloured by hand.
 *
 * @param {Interactor} interactor The interactor to classify.
 * @returns {boolean} True when clicking it accepts the call.
 */
export function isAcceptInteractor(interactor?: Interactor): boolean {
  if (!interactor || interactor.Kind !== "button") return false;
  if (normalizeColor(interactor.Color) === ACCEPT_COLOR) return true;
  return /\b(?:accept|confirm)\b/i.test(interactor.Name ?? "");
}

/**
 * Finds the Hold interactor offered alongside Accept, if the stage has one.
 *
 * Its absence is meaningful: stages that only offer Accept and Decline (a
 * musician already on hold, an assignment acknowledgement) have no third option
 * to steer anyone towards, so the guard stays out of the way there.
 *
 * @param {Interactor[]} interactors The stage's interactors.
 * @returns {Interactor | undefined} The Hold interactor, when present.
 */
export function findHoldInteractor(
  interactors?: Interactor[],
): Interactor | undefined {
  const buttons = (interactors ?? []).filter((i) => i.Kind === "button");
  return (
    buttons.find((i) => /\bhold\b/i.test(i.Name ?? "")) ??
    buttons.find(
      (i) =>
        normalizeColor(i.Color) === HOLD_COLOR &&
        !/\b(?:accept|confirm|decline|acknowledge)\b/i.test(i.Name ?? ""),
    )
  );
}
